import { makeAutoObservable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import type { TGanttDependency, TGanttDependencyType } from "@/types/gantt";
import type { TDependencySubtype } from "@/types/issue";

export class TimelineStore {
  activeBlockId: string | null = null;
  isFullscreen = false;
  dependencies: TGanttDependency[] = [];
  draggingDependencyFrom: string | null = null;
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  setActiveBlock(id: string | null): void {
    this.activeBlockId = id;
  }

  setFullscreen(on: boolean): void {
    this.isFullscreen = on;
  }

  setDependencies(deps: TGanttDependency[]): void {
    this.dependencies = deps;
  }

  beginDependencyDrag(sourceIssueId: string): void {
    this.draggingDependencyFrom = sourceIssueId;
  }

  cancelDependencyDrag(): void {
    this.draggingDependencyFrom = null;
  }

  async fetchDependencies(slug: string, projectId: string): Promise<void> {
    const deps = await this.rootStore.workItemService.listProjectRelations(slug, projectId);
    runInAction(() => {
      this.dependencies = deps;
    });
  }

  wouldCreateCycle(sourceIssueId: string, targetIssueId: string): boolean {
    if (sourceIssueId === targetIssueId) return true;
    const graph = new Map<string, string[]>();
    for (const d of this.dependencies) {
      const list = graph.get(d.sourceIssueId) ?? [];
      list.push(d.targetIssueId);
      graph.set(d.sourceIssueId, list);
    }
    const queue: string[] = [targetIssueId];
    const seen = new Set<string>();
    while (queue.length) {
      const node = queue.shift()!;
      if (node === sourceIssueId) return true;
      if (seen.has(node)) continue;
      seen.add(node);
      for (const next of graph.get(node) ?? []) queue.push(next);
    }
    return false;
  }

  async createDependency(
    slug: string,
    projectId: string,
    sourceIssueId: string,
    targetIssueId: string,
    relationType: TGanttDependencyType,
    subtype?: TDependencySubtype | null,
    source: AnalyticsSource = "gantt",
  ): Promise<void> {
    // Only blocked_by carries a subtype; relates_to has none.
    const resolvedSubtype = relationType === "blocked_by" ? (subtype ?? null) : null;
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const prior = this.dependencies;
    runInAction(() => {
      this.dependencies = [
        ...prior,
        {
          id: tempId,
          sourceIssueId,
          targetIssueId,
          relation_type: relationType,
          subtype: resolvedSubtype,
        },
      ];
    });

    const anchorIssueId = relationType === "blocked_by" ? targetIssueId : sourceIssueId;
    const relatedIssueId = relationType === "blocked_by" ? sourceIssueId : targetIssueId;

    try {
      await this.rootStore.workItemService.createRelations(slug, projectId, anchorIssueId, {
        relation_type: relationType,
        related_list: [
          { relation_type: relationType, related_issue: relatedIssueId, subtype: resolvedSubtype },
        ],
      });
      // Before the refresh: the dependency exists even if reloading them fails.
      track("dependency_created", {
        project_id: projectId,
        source_task_id: sourceIssueId,
        target_task_id: targetIssueId,
        relation_type: relationType,
        source,
      });
      await this.fetchDependencies(slug, projectId);
    } catch (error) {
      runInAction(() => {
        this.dependencies = prior;
      });
      throw error;
    }
  }

  async deleteDependency(
    slug: string,
    projectId: string,
    relationId: string,
    source: AnalyticsSource = "gantt",
  ): Promise<void> {
    const dep = this.dependencies.find((d) => d.id === relationId);
    if (!dep) return;
    const prior = this.dependencies;
    runInAction(() => {
      this.dependencies = prior.filter((d) => d.id !== relationId);
    });
    const anchorIssueId =
      dep.relation_type === "blocked_by" ? dep.targetIssueId : dep.sourceIssueId;
    const relatedIssueId =
      dep.relation_type === "blocked_by" ? dep.sourceIssueId : dep.targetIssueId;
    try {
      await this.rootStore.workItemService.removeRelation(
        slug,
        projectId,
        anchorIssueId,
        relatedIssueId,
        dep.relation_type,
      );
      track("dependency_deleted", {
        project_id: projectId,
        source_task_id: dep.sourceIssueId,
        target_task_id: dep.targetIssueId,
        relation_type: dep.relation_type,
        source,
      });
    } catch (error) {
      runInAction(() => {
        this.dependencies = prior;
      });
      throw error;
    }
  }
}
